import { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../AppContext";
import { ConsentPolicyType, UserPhoneCheckStatus } from "../../enums";
import { useApiMutation } from "./useApiMutation";
import {
  ILoginFormModel,
  loginFormDefaultValues,
  getLoginSchema,
} from "../../../components/authentication/models/loginFormModel";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getAllErrorMessages } from "../utilities/formsUtility";
import { getErrorToast, getSuccessToast } from "../utilities/toastUtility";
import { normalizePhone } from "../../components/forms/inputs/phoneField/utils/PhoneInputUtility";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import { IUserModel } from "./useAuthentication";
import { useApiQuery } from "./useApiQuery";
import { IPolicyViewModel } from "../../../components/authentication/models/policyViewModel";
import { showCustomConfirmDialog } from "../../components/dialogs/AppConfirmDialog";
import { useAuthenticationFunctions } from "../../../components/authentication/utils/AuthenticationUtility";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { ICheckPhoneNumberResultModel } from "../../../components/authentication/models/checkPhoneNumberResultModel";

interface IUseAuthenticationFormOptions {
  onSuccessfulLogin?: (user: IUserModel) => void;
  showToasts?: boolean;
  returnUrl?: string;
}

export const useAuthenticationForm = (
  options: IUseAuthenticationFormOptions = {}
) => {
  const { selectedStudio, isMobile, isDesktopOrTablet } = useAppContext();
  const [status, setStatus] = useState<UserPhoneCheckStatus>();
  const [isForgottenPasswordMode, setIsForgottenPasswordMode] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string>("");
  const [hasEmail, setHasEmail] = useState<boolean>(true);
  const [checkedPhoneNumber, setCheckedPhoneNumber] = useState<string>("");
  const { mutateAsync } = useApiMutation();
  const { onSuccessfulUserLogin } = useAuthenticationFunctions();
  const signIn = useSignIn();

  const { showToasts = true, onSuccessfulLogin, returnUrl } = options;

  const { data: policies } = useApiQuery<IPolicyViewModel[]>({
    queryKey: [
      endpointsMetadata.authentication.getAllPolicies,
      selectedStudio?.id,
    ],
    endpoint: `${endpointsMetadata.authentication.getAllPolicies}?studioId=${selectedStudio?.id}`,
    enabled: !!selectedStudio,
    globalIsLoading: false,
    retry: false,
  });

  const termsOfUseAvailable = useMemo(
    () =>
      !!policies?.find((x) => x.type === ConsentPolicyType.TenantTermsOfUse)
        ?.policy,
    [policies]
  );

  const form = useForm<ILoginFormModel>({
    resolver: yupResolver(
      getLoginSchema(termsOfUseAvailable, status, isGoogleUser, hasEmail, isForgottenPasswordMode)
    ),
    defaultValues: loginFormDefaultValues,
  });

  const { errors } = form.formState;
  const phoneNumberWatch = form.watch("phoneNumber");

  useEffect(() => {
    const normalizedNew = normalizePhone(phoneNumberWatch) ?? "";
    if (normalizedNew === checkedPhoneNumber) return;

    setStatus(undefined);
    setIsForgottenPasswordMode(false);
    setIsGoogleUser(false);
    setIsLoading(false);
    setMaskedEmail("");
    setHasEmail(true);
    setCheckedPhoneNumber("");
    form.setValue("email", "");
    form.setValue("firstName", "");
    form.setValue("lastName", "");
    form.setValue("birthDate", null);
    form.setValue("password", "");
    form.setValue("confirmPassword", "");
    form.setValue("verificationCode", "");
    form.setValue("acceptMarketingEmails", false);
    form.setValue("acceptAutomatedEmails", false);
    form.setValue("acceptTermsOfUse", false);
    form.setValue("acceptPrivacyPolicy", false);
  }, [phoneNumberWatch, form]);

  useEffect(() => {
    if (showToasts && Object.keys(errors).length > 0) {
      getErrorToast(getAllErrorMessages<ILoginFormModel>(errors));
    }
  }, [errors, showToasts]);

  const validatePhoneNumber = async (data: ILoginFormModel) => {
    setIsLoading(true);
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    try {
      setIsLoading(true);
      await mutateAsync(
        {
          endpoint: endpointsMetadata.authentication.checkPhoneNumber,
          method: "post",
          disableSuccessToast: true,
          ...data,
        },
        {
          onSuccess: async (data) => {
            const result = data as ICheckPhoneNumberResultModel;
            setStatus(result.status);
            setIsGoogleUser(result.isGoogleUser);
            setHasEmail(result.hasEmail ?? true);
            setCheckedPhoneNumber(normalizePhone(form.getValues("phoneNumber")) ?? "");

            // A new code kills the one already in the inbox, so one is sent only when no code is live.
            if (result.status === UserPhoneCheckStatus.PendingVerification) {
              setMaskedEmail(result.maskedEmail ?? "");
              if (!result.hasActiveVerificationCode) {
                await sendCodeForPendingVerification(form.getValues("phoneNumber"));
              }
            }
          },
          onSettled: () => {
            setIsLoading(false);
          },
        }
      );
    } catch {}
  };

  const sendCodeForPendingVerification = async (phoneNumber: string) => {
    try {
      await mutateAsync({
        endpoint: endpointsMetadata.authentication.resendVerificationCode,
        method: "post",
        disableSuccessToast: true,
        globalIsLoading: false,
        payload: { phoneNumber },
        queryParams: { studioId: selectedStudio?.id },
      });
    } catch {}
  };

  const sendRegistrationCode = async (data: ILoginFormModel) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    try {
      setIsLoading(true);
      await mutateAsync(
        {
          endpoint: endpointsMetadata.authentication.sendRegistrationCode,
          method: "post",
          disableSuccessToast: true,
          globalIsLoading: false,
          payload: data,
          queryParams: { studioId: selectedStudio?.id },
        },
        {
          onSuccess: (data: any) => {
            setMaskedEmail(data?.maskedEmail ?? "");
            setStatus(UserPhoneCheckStatus.PendingVerification);
          },
          onSettled: () => {
            setIsLoading(false);
          },
        }
      );
    } catch {}
  };

  const sendFirstLoginCode = async (data: ILoginFormModel) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    try {
      setIsLoading(true);
      await mutateAsync(
        {
          endpoint: endpointsMetadata.authentication.sendFirstLoginCode,
          method: "post",
          disableSuccessToast: true,
          globalIsLoading: false,
          payload: {
            phoneNumber: data.phoneNumber,
            ...(!hasEmail && data.email ? { email: data.email } : {}),
            password: data.password,
            confirmPassword: data.confirmPassword,
          },
          queryParams: { studioId: selectedStudio?.id },
        },
        {
          onSuccess: (result: any) => {
            if (result?.isEmailPreConfirmed) {
              initiateLogin(data);
            } else {
              setMaskedEmail(result?.maskedEmail ?? "");
              setStatus(UserPhoneCheckStatus.PendingVerification);
            }
          },
          onSettled: () => {
            setIsLoading(false);
          },
        }
      );
    } catch {}
  };

  const validateEmail = async (data: ILoginFormModel) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    try {
      setIsLoading(true);
      await mutateAsync(
        {
          endpoint: endpointsMetadata.authentication.sendPasswordSetEmail,
          method: "post",
          disableSuccessToast: true,
          globalIsLoading: false,
          ...data,
        },
        {
          onSuccess: (data) => {
            const status = data as UserPhoneCheckStatus;
            setStatus(status);

            if (showToasts) {
              showCustomConfirmDialog(
                "<b>Изпратихме Ви имейл</b> с <b>линк</b> за създаване на парола.<br/><br/> Ако не го откривате, проверете в папка <b>СПАМ</b>.",
                undefined,
                () => {},
                "success",
                "Добре",
                null
              );
            }
          },
          onSettled: () => {
            setIsLoading(false);
          },
        }
      );
    } catch {}
  };

  const sendForgottenPasswordEmail = async (data: ILoginFormModel) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    try {
      setIsLoading(true);
      await mutateAsync(
        {
          endpoint: endpointsMetadata.authentication.forgottenPassword,
          method: "post",
          disableSuccessToast: true,
          globalIsLoading: false,
          ...data,
        },
        {
          onSuccess: () => {
            setStatus(undefined);
            setIsForgottenPasswordMode(false);
            form.setValue("email", "");
            if (showToasts) {
              getSuccessToast(
                "Изпратихме Ви имейл с линк за възстановяване на паролата. Ако не го откривате, проверете в папка СПАМ."
              );
            }
          },
          onSettled: () => {
            setIsLoading(false);
          },
        }
      );
    } catch {}
  };

  const initiateLogin = async (data: ILoginFormModel) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    try {
      setIsLoading(true);
      await mutateAsync(
        {
          endpoint: endpointsMetadata.authentication.login,
          globalIsLoading: false,
          method: "post",
          disableSuccessToast: true,
          disable401Redirect: true,
          ...data,
        },
        {
          onSuccess: async (data) => {
            const currentUser = data as IUserModel;
            if (onSuccessfulLogin) {
              const { permissions, ...userWithoutPermissions } = currentUser;
              localStorage.setItem("permissions", JSON.stringify(permissions));

              const signInResult = signIn({
                auth: {
                  token: currentUser.authToken,
                  type: "Bearer",
                },
                userState: userWithoutPermissions,
              });

              if (signInResult) {
                requestAnimationFrame(() => {
                  onSuccessfulLogin(currentUser);
                });
              } else {
                console.error(
                  "[useAuthenticationForm] Failed to update auth state"
                );
                if (showToasts) {
                  getErrorToast("Грешка при влизане. Моля опитайте отново.");
                }
              }
            } else {
              onSuccessfulUserLogin(currentUser, returnUrl);
            }
          },
          onSettled: () => {
            setIsLoading(false);
          },
        }
      );
    } catch {}
  };

  const onSave: SubmitHandler<ILoginFormModel> = async (data) => {
    switch (status) {
      case undefined:
        await validatePhoneNumber(data);
        break;
      case UserPhoneCheckStatus.BrandNew:
        sendRegistrationCode(data);
        break;
      case UserPhoneCheckStatus.FirstLogin:
        sendFirstLoginCode(data);
        break;
      case UserPhoneCheckStatus.Regular:
        if (isForgottenPasswordMode) {
          sendForgottenPasswordEmail(data);
        } else {
          initiateLogin(data);
        }
        break;
      default:
        break;
    }
  };

  const inputMaxWidth = isMobile ? "100%" : "350px";

  const setLoginStep = () => {
    setStatus(UserPhoneCheckStatus.Regular);
  };

  const onForgottenPasswordClick = () => {
    setIsForgottenPasswordMode(true);
  };

  const resetForm = () => {
    form.reset(loginFormDefaultValues);
    setStatus(undefined);
    setIsForgottenPasswordMode(false);
    setIsGoogleUser(false);
    setIsLoading(false);
    setMaskedEmail("");
    setHasEmail(true);
    setCheckedPhoneNumber("");
  };

  const handleVerifiedUser = (currentUser: IUserModel) => {
    const { permissions, ...userWithoutPermissions } = currentUser;
    localStorage.setItem("permissions", JSON.stringify(permissions));

    if (onSuccessfulLogin) {
      const signInResult = signIn({
        auth: { token: currentUser.authToken, type: "Bearer" },
        userState: userWithoutPermissions,
      });

      if (signInResult) {
        requestAnimationFrame(() => onSuccessfulLogin(currentUser));
      } else {
        if (showToasts) {
          getErrorToast("Грешка при влизане. Моля опитайте отново.");
        }
      }
    } else {
      onSuccessfulUserLogin(currentUser, returnUrl);
    }
  };

  return {
    form,
    inputMaxWidth,
    status,
    maskedEmail,
    hasEmail,
    isForgottenPasswordMode,
    policies,
    isDesktopOrTablet,
    isLoading,
    termsOfUseAvailable,
    isGoogleUser,
    selectedStudio,
    askForUserBirthdayOnRegistration: selectedStudio?.askForUserBirthdayOnRegistration ?? false,
    isGoogleLoginDisabled: selectedStudio?.disableGoogleLogin ?? false,
    onSave,
    setLoginStep,
    onForgottenPasswordClick,
    resetForm,

    handleVerifiedUser,

    // Additional methods for more control
    validatePhoneNumber,
    validateEmail,
    sendForgottenPasswordEmail,
    initiateLogin,
  };
};
